/**
 * A member's agency roles, chosen in a modal of their own from the grid.
 *
 * Every role the operator may pick, grouped, in two columns, each tile with
 * its description. Pointing at a tile (or moving onto it with the keys) opens
 * the areas of the app the role lets somebody change or see.
 *
 * Unlike the cells around it this one has a Save: clicking around to see
 * which roles cover which is natural here, and saving on every click would
 * write (and audit) each of those passing states as a real grant.
 */

import React, { useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Check } from 'lucide-react';
import type { RoleResponse } from '../../../services/api/types.gen';
import type { OrgMemberResponse } from '../types';
import { groupRoles, visiblePermissions } from '../../../utils/roleGroups';
import {
  type AreaSummary,
  summarizePermissions,
} from '../../../utils/permissionAreas';
import { memberDisplayName } from './clinicSetup';

/**
 * What a role does: the areas where somebody can change things, and those
 * they can only see, with short action descriptions visible under each area.
 */
const Areas: React.FC<{ areas: AreaSummary[] }> = ({ areas }) => {
  const row = (label: string, list: AreaSummary[], changes: boolean) =>
    list.length > 0 && (
      <div className='space-y-1.5'>
        <p className='text-xs font-medium text-muted-foreground'>{label}</p>
        <div className='space-y-1.5'>
          {list.map(area => {
            const descriptions = area.actions
              .map(a => a.description || a.label)
              .filter(Boolean)
              .join(' · ');

            return (
              <div key={area.key} className='text-xs'>
                <Badge
                  variant={changes ? 'secondary' : 'outline'}
                  className='font-normal text-xs mb-0.5'
                >
                  {area.label}
                </Badge>
                {descriptions && (
                  <p className='text-[11px] leading-snug text-muted-foreground pl-0.5'>
                    {descriptions}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  return (
    <div className='space-y-3'>
      {row(
        'Can change',
        areas.filter(a => a.changes),
        true
      )}
      {row(
        'Can see',
        areas.filter(a => !a.changes),
        false
      )}
    </div>
  );
};

/** What one role is for and what it does: the tile's tooltip. */
const RoleDetail: React.FC<{ role: RoleResponse }> = ({ role }) => {
  const permissions = visiblePermissions(role);
  return (
    <div>
      <div className='rounded-md border bg-muted/40 p-3'>
        <h3 className='text-sm font-semibold'>{role.name}</h3>
        {role.description && (
          <p className='mt-0.5 text-sm text-muted-foreground'>
            {role.description}
          </p>
        )}
      </div>
      <div className='mt-3'>
        {permissions.length ? (
          <Areas areas={summarizePermissions(permissions)} />
        ) : (
          <p className='text-sm text-muted-foreground'>Sign in only.</p>
        )}
      </div>
    </div>
  );
};

export const MemberAgencyRolesDialog: React.FC<{
  member: OrgMemberResponse;
  /** The agency roles the operator may pick from, and no others. */
  roles: RoleResponse[];
  selectedIds: ReadonlySet<string>;
  /** The roles picked, when Save is pressed. */
  onSave: (roleIds: string[]) => void;
  onClose: () => void;
  /** Where focus goes when it closes (the grid, so its keys keep working). */
  onCloseAutoFocus?: (event: Event) => void;
}> = ({ member, roles, selectedIds, onSave, onClose, onCloseAutoFocus }) => {
  const groups = useMemo(() => groupRoles(roles), [roles]);
  const ordered = useMemo(() => groups.flatMap(g => g.roles), [groups]);

  const [picked, setPicked] = useState<ReadonlySet<string>>(
    () => new Set(selectedIds)
  );
  // The tile the keyboard is on, and the one whose explanation is open.
  const [activeId, setActiveId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  /** Tiles are all one width; read from the one last pointed at. */
  const [tileWidth, setTileWidth] = useState(200);
  const listRef = useRef<HTMLDivElement>(null);

  const changed =
    picked.size !== selectedIds.size ||
    [...picked].some(id => !selectedIds.has(id));

  const toggle = (roleId: string) => {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  };
  const save = () => {
    if (changed) onSave([...picked]);
  };

  const active = ordered.find(role => role.id === activeId) ?? null;

  const onListKeyDown = (e: React.KeyboardEvent) => {
    const index = ordered.findIndex(role => role.id === activeId);
    const move = (to: number) => {
      e.preventDefault();
      const next = ordered[Math.max(0, Math.min(ordered.length - 1, to))];
      if (!next) return;
      setActiveId(next.id);
      setOpenId(next.id);
      listRef.current
        ?.querySelector(`[data-role-id="${next.id}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    };
    if (e.key === 'ArrowDown') move(index < 0 ? 0 : index + 1);
    else if (e.key === 'ArrowUp') move(index < 0 ? 0 : index - 1);
    else if (e.key === ' ' && active) {
      e.preventDefault();
      toggle(active.id);
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent
        className='flex max-h-[85vh] flex-col sm:max-w-2xl'
        onOpenAutoFocus={e => {
          e.preventDefault();
          listRef.current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
        onKeyDown={e => {
          // Enter saves from anywhere but a button, which has its own Enter.
          if (
            e.key === 'Enter' &&
            (e.target as HTMLElement).tagName !== 'BUTTON'
          ) {
            e.preventDefault();
            save();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{memberDisplayName(member)}</DialogTitle>
        </DialogHeader>

        {/* Explanations open quickly: pointing along the tiles is how
            somebody compares roles. */}
        <TooltipProvider
          delayDuration={150}
          skipDelayDuration={300}
          // Closes as the pointer leaves the tile, and never takes a click:
          // the tiles it covers stay clickable.
          disableHoverableContent
        >
          <div
            ref={listRef}
            role='listbox'
            aria-label='Agency roles'
            aria-multiselectable
            tabIndex={0}
            onKeyDown={onListKeyDown}
            className='min-h-0 flex-1 overflow-y-auto p-1 outline-none'
          >
            {groups.map(group => (
              <section key={group.title} className='mb-3'>
                <h4 className='px-1 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground'>
                  {group.title}
                </h4>
                <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                  {group.roles.map(role => {
                    const ticked = picked.has(role.id);
                    return (
                      <Tooltip
                        key={role.id}
                        open={openId === role.id}
                        onOpenChange={open =>
                          setOpenId(prev =>
                            open ? role.id : prev === role.id ? null : prev
                          )
                        }
                      >
                        <TooltipTrigger asChild>
                          <div
                            role='option'
                            aria-selected={ticked}
                            data-role-id={role.id}
                            onMouseEnter={e => {
                              setActiveId(role.id);
                              setTileWidth(e.currentTarget.offsetWidth);
                            }}
                            onClick={() => toggle(role.id)}
                            // The member dialog's card, description and all.
                            // The tile under the keyboard highlight looks
                            // hovered.
                            className={`flex min-w-0 cursor-pointer items-start gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                              ticked
                                ? 'border-primary bg-primary/5'
                                : activeId === role.id
                                  ? 'border-primary/40 bg-muted/40'
                                  : 'border-border bg-card'
                            }`}
                          >
                            <span
                              className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                ticked
                                  ? 'border-primary bg-primary text-primary-foreground'
                                  : 'border-input'
                              }`}
                            >
                              {ticked && <Check className='h-3 w-3' />}
                            </span>
                            <span className='min-w-0'>
                              <span className='block truncate font-medium leading-snug'>
                                {role.name}
                              </span>
                              {role.description && (
                                <span className='block truncate text-xs text-muted-foreground'>
                                  {role.description}
                                </span>
                              )}
                            </span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          // Above the tile and running right, clear of the
                          // row being pointed along; it flips when the screen
                          // edge is too close.
                          side='top'
                          align='start'
                          // Starts near the tile's right end, so it sits off
                          // its top-right corner.
                          alignOffset={Math.round(tileWidth * 0.75)}
                          sideOffset={14}
                          collisionPadding={12}
                          className='pointer-events-none w-80 p-3'
                        >
                          <RoleDetail role={role} />
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </TooltipProvider>

        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button type='button' disabled={!changed} onClick={save}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
